'use client';

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Database, Radio, Server, Wifi } from 'lucide-react';
import {
  DEPENDENCY_LABEL,
  DEPENDENCY_STATE_LABEL,
  dependencyTooltip,
  type DependencyKey,
  type DependencyStateKey,
} from '@/lib/copy';

export type DependencyStates = Record<DependencyKey, DependencyStateKey>;

/** Worst-first, because that is the order the verdict is decided in. */
const SEVERITY: DependencyStateKey[] = ['disconnected', 'unknown', 'connected'];

/**
 * The `-marker` variants, not the base hues. `globals.css` keeps a separate
 * contrast-tuned set for exactly this - "non-text status graphics (legend dots,
 * severity stripes, gauge fills) need >=3:1 against the page surface (SC
 * 1.4.11)" - measured per theme, where the base hues are not.
 */
const DOT_COLOR: Record<DependencyStateKey, string> = {
  disconnected: 'bg-status-red-marker',
  unknown: 'bg-status-amber-marker',
  connected: 'bg-status-green-marker',
};

const ICON_COLOR: Record<DependencyStateKey, string> = {
  disconnected: 'text-status-red-fg',
  unknown: 'text-status-amber-fg',
  connected: 'text-status-green-fg',
};

const ICONS: Record<DependencyKey, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  db: Database,
  cache: Server,
  mqtt: Radio,
  updates: Wifi,
};

/**
 * The single worst state across the four dependencies.
 *
 * Exported for the test, and because "worst" is the whole contract: a dot that
 * averaged, or that reported the first one it found, would show green with MQTT
 * down and the operator would learn nothing until a command failed to arrive.
 */
export function worstState(states: DependencyStates): DependencyStateKey {
  const values = Object.values(states);
  return SEVERITY.find((s) => values.includes(s)) ?? 'connected';
}

/** How long a pointer must rest before the four open, in ms. */
const HOVER_INTENT_MS = 200;

/**
 * The four subsystem icons, collapsed to one dot (amendment E of the declutter
 * plan).
 *
 * They were four things the eye processed in the top-right corner of a tactical
 * display, and an operator does not act on any of them - they are what the team
 * reads when the operator says "it has stopped updating". So the resting state
 * is one dot the colour of the worst of them, and the four are one hover or one
 * press away.
 *
 * The expansion is deliberately not a CSS `group-hover` reveal like the tooltip
 * it replaces: there is no hover on an RC screen or a tablet, and a detail an
 * operator cannot reach on the display they are flying from is not a detail.
 */
export function SystemHealthDot({ states }: { states: DependencyStates }) {
  const [open, setOpen] = useState(false);
  /** Opened by a press: a pointer leaving must not close it under a finger. */
  const [pinned, setPinned] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const intentRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const worst = worstState(states);
  const summary = dependencyTooltip(states);

  useEffect(() => () => clearTimeout(intentRef.current), []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setPinned(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Escape' || !open) return;
    e.stopPropagation();
    setOpen(false);
    setPinned(false);
    triggerRef.current?.focus();
  }

  return (
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={handleKeyDown}
      onPointerEnter={() => {
        // An intent delay, for the same reason the floating rail has one: this
        // sits in a row an operator's pointer crosses on the way to the clock,
        // the alert bell and the site picker, and a panel that opens on the way
        // past is a panel that opens by accident.
        clearTimeout(intentRef.current);
        intentRef.current = setTimeout(() => setOpen(true), HOVER_INTENT_MS);
      }}
      onPointerLeave={() => {
        clearTimeout(intentRef.current);
        if (!pinned) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        // The name carries the whole verdict, not just "system health": a screen
        // reader gets what the four dots used to say, from the one control that
        // replaced them.
        aria-label={summary}
        title={summary}
        // Activation, not focus. Opening on focus made Escape dead: it closes
        // and hands focus back to this button, which re-fired the focus handler
        // and re-opened the panel. A button with `aria-expanded` is expected to
        // open on activation anyway, so Enter and Space already reach it.
        // ONE value drives both. Toggling them independently desynced whenever
        // the panel was already open from hover: a click then set open=false
        // with pinned=TRUE, and from there every later hover opened a panel that
        // pointer-leave would no longer close.
        onClick={() => {
          clearTimeout(intentRef.current);
          const next = !open;
          setOpen(next);
          setPinned(next);
        }}
        // 24 px, which is the WCAG 2.5.8 minimum, around a 10 px dot. The dot it
        // replaces was a 14 px icon with no hit target of its own at all.
        //
        // A hover and focus ground, because a bare dot says nothing about being
        // pressable - and on a touch display there is no tooltip to discover it
        // with either. The same affordance the site picker and the Missions key
        // already carry.
        className="flex size-6 items-center justify-center rounded-full transition-colors hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          data-testid="system-health-dot"
          data-state={worst}
          className={`block size-2.5 rounded-full ${DOT_COLOR[worst]}`}
        />
      </button>

      {open && (
        <div
          id={panelId}
          role="group"
          aria-label={summary}
          className="absolute right-0 top-full z-50 mt-1.5 min-w-[170px] rounded-md border border-border bg-popover px-2.5 py-2 text-[11px] shadow-lg"
        >
          <ul className="flex flex-col gap-1.5">
            {(Object.keys(DEPENDENCY_LABEL) as DependencyKey[]).map((key) => {
              const Icon = ICONS[key];
              const state = states[key];
              return (
                <li key={key} className="flex items-center gap-2 whitespace-nowrap">
                  <Icon className={`size-3.5 shrink-0 ${ICON_COLOR[state]}`} strokeWidth={1.5} />
                  <span className="text-muted-foreground">{DEPENDENCY_LABEL[key]}</span>
                  <span className={`ml-auto ${ICON_COLOR[state]}`}>{DEPENDENCY_STATE_LABEL[state]}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
